import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';
import { IncomingDocumentStatus, IncomingDocumentType } from 'src/types/enums';

import type { IncomingDocumentsResponse } from './endpoints/IncomingDocumentsEndpoint.types';
import type { IncomingDocumentDetail } from './endpoints/IncomingDocumentEndpoint.types';
import type { IncomingDocumentDebugResponse } from './endpoints/IncomingDocumentDebugEndpoint.types';
import type { PendingIncomingDocumentsCountResponse } from './endpoints/PendingIncomingDocumentsCountEndpoint.types';
import type { MinimalCmrOrder, MinimalCmrOrdersResponse } from './endpoints/MinimalCmrOrdersEndpoint.types';
import type { MinimalPurchase, MinimalPurchasesResponse } from './endpoints/MinimalPurchasesEndpoint.types';
import type { IncomingDocIdRef, IncomingDocIdRefList, IncomingDocumentPageRange } from './endpoints/mutation.types';

// Cleaner re-exports for consumers.
export type IncomingDocument = IncomingDocumentDetail;
export type { MinimalCmrOrder, MinimalPurchase };

export const INCOMING_DOCUMENT_SWR_KEY = 'incoming-document';
export const PENDING_INCOMING_DOCUMENTS_COUNT_SWR_KEY = 'pending-incoming-documents-count';
export const MINIMAL_CMR_ORDERS_SWR_KEY = 'minimal-cmr-orders';
export const MINIMAL_PURCHASES_SWR_KEY = 'minimal-purchases';

const BASE = '/api/v1/incoming-documents';

// --- Queries ---

export interface IncomingDocumentsFilters {
  status?: IncomingDocumentStatus[];
  approvedManually?: boolean;
}

export async function getIncomingDocuments(
  cursor?: string | null,
  take?: number,
  filters?: IncomingDocumentsFilters,
): Promise<IncomingDocumentsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set('id', cursor);
  if (take != null) params.set('take', take.toString());
  if (filters?.status) {
    for (const status of filters.status) {
      params.append('status', status);
    }
  }
  if (filters?.approvedManually != null) {
    params.set('approvedManually', filters.approvedManually ? 'true' : 'false');
  }

  const queryString = params.toString();
  const url = queryString ? `${BASE}?${queryString}` : BASE;
  return fetchEndpointData<IncomingDocumentsResponse>(url);
}

export async function getIncomingDocument(id: string): Promise<IncomingDocumentDetail> {
  return fetchEndpointData<IncomingDocumentDetail>(`${BASE}/${id}`);
}

export async function getIncomingDocumentDebug(id: string): Promise<IncomingDocumentDebugResponse> {
  return fetchEndpointData<IncomingDocumentDebugResponse>(`${BASE}/${id}/debug`);
}

export async function getPendingIncomingDocumentsCount(): Promise<PendingIncomingDocumentsCountResponse> {
  return fetchEndpointData<PendingIncomingDocumentsCountResponse>(`${BASE}/pending-count`);
}

export interface MinimalCmrOrdersFilters {
  orderNumber?: string;
}

export async function getMinimalCmrOrders(
  take?: number,
  filters?: MinimalCmrOrdersFilters,
): Promise<MinimalCmrOrdersResponse> {
  const params = new URLSearchParams();
  if (take != null) params.set('take', take.toString());
  if (filters?.orderNumber) params.set('orderNumber', filters.orderNumber);

  const queryString = params.toString();
  const url = queryString ? `${BASE}/minimal-cmr-orders?${queryString}` : `${BASE}/minimal-cmr-orders`;
  return fetchEndpointData<MinimalCmrOrdersResponse>(url);
}

export interface MinimalPurchasesFilters {
  search?: string;
}

export async function getMinimalPurchases(
  take?: number,
  filters?: MinimalPurchasesFilters,
): Promise<MinimalPurchasesResponse> {
  const params = new URLSearchParams();
  if (take != null) params.set('take', take.toString());
  if (filters?.search) params.set('search', filters.search);

  const queryString = params.toString();
  const url = queryString ? `${BASE}/minimal-purchases?${queryString}` : `${BASE}/minimal-purchases`;
  return fetchEndpointData<MinimalPurchasesResponse>(url);
}

// --- Mutations (async fetchers; callers wrap in try/catch) ---

export async function linkIncomingDocumentToOrder(incomingDocId: string, orderId: string): Promise<IncomingDocIdRef> {
  return fetchEndpointData<IncomingDocIdRef>(`${BASE}/${incomingDocId}/link-order`, {
    method: 'POST',
    body: { orderId },
  });
}

export async function linkIncomingDocumentToPurchase(
  incomingDocId: string,
  purchaseId: string,
): Promise<IncomingDocIdRef> {
  return fetchEndpointData<IncomingDocIdRef>(`${BASE}/${incomingDocId}/link-purchase`, {
    method: 'POST',
    body: { purchaseId },
  });
}

export async function rejectIncomingDocument(incomingDocId: string): Promise<IncomingDocIdRef> {
  return fetchEndpointData<IncomingDocIdRef>(`${BASE}/${incomingDocId}/reject`, {
    method: 'POST',
  });
}

export async function approveIncomingDocument(incomingDocId: string): Promise<IncomingDocIdRef> {
  return fetchEndpointData<IncomingDocIdRef>(`${BASE}/${incomingDocId}/approve`, {
    method: 'POST',
  });
}

export async function updateIncomingDocumentType(
  incomingDocId: string,
  newType: IncomingDocumentType,
): Promise<IncomingDocIdRef> {
  return fetchEndpointData<IncomingDocIdRef>(`${BASE}/${incomingDocId}/update-type`, {
    method: 'POST',
    body: { newType },
  });
}

export async function predictIncomingInvoice(incomingDocId: string): Promise<IncomingDocIdRef> {
  return fetchEndpointData<IncomingDocIdRef>(`${BASE}/${incomingDocId}/predict-invoice`, {
    method: 'POST',
  });
}

export async function splitIncomingDocument(
  incomingDocId: string,
  ranges: IncomingDocumentPageRange[],
): Promise<IncomingDocIdRefList> {
  return fetchEndpointData<IncomingDocIdRefList>(`${BASE}/${incomingDocId}/split`, {
    method: 'POST',
    body: { ranges },
  });
}

export async function uploadManualIncomingDocument(documentId: string): Promise<IncomingDocIdRefList> {
  return fetchEndpointData<IncomingDocIdRefList>(`${BASE}/upload-manual`, {
    method: 'POST',
    body: { documentId },
  });
}

// --- Hooks ---

export function useIncomingDocument(id: string) {
  return useSWR([INCOMING_DOCUMENT_SWR_KEY, id], () => getIncomingDocument(id));
}

export function usePendingIncomingDocumentsCount() {
  return useSWR([PENDING_INCOMING_DOCUMENTS_COUNT_SWR_KEY], () => getPendingIncomingDocumentsCount());
}

export function useMinimalCmrOrders(searchValue: string, take = 500) {
  return useSWR([MINIMAL_CMR_ORDERS_SWR_KEY, searchValue, take], () =>
    getMinimalCmrOrders(take, { orderNumber: searchValue }),
  );
}

export function useMinimalPurchases(searchValue: string, take = 50) {
  return useSWR([MINIMAL_PURCHASES_SWR_KEY, searchValue, take], () =>
    getMinimalPurchases(take, { search: searchValue }),
  );
}
